// =============================================================================
// ui/charts/Heatmap.tsx — the Day × Hour grid.
// -----------------------------------------------------------------------------
// Relocated from the old client's `renderHeatmap`. A CSS grid, not an SVG —
// which is what beta uses, and it is the right call here: every cell carries
// text, and text in a stretched SVG is the trap D81 records.
//
// ⚠ `56px repeat(N, minmax(0, 1fr))`, and `minmax(0, …)` rather than a bare
// `1fr` is load-bearing. Beta's own comment: with `1fr` alone, one oversized
// value ("+$1.3k") expands its column and pushes the whole grid past the card's
// right edge. A grid track's default `min-width: auto` refuses to shrink below
// its content — the same footgun as the Setups kanban.
// =============================================================================

import {
  DOW_LABELS, cellAlpha, cellKey, cellLabel, hourLabel, type HeatmapGrid,
} from '../../selectors/heatmap';

export interface HeatmapProps {
  grid: HeatmapGrid;
  /** Formats a cell's exact P&L for the tooltip, and small cells' labels. */
  money: (n: number) => string;
}

export function Heatmap({ grid, money }: HeatmapProps) {
  const { dows, hours, cells, maxAbs } = grid;

  return (
    <div
      className="heatmap-grid"
      style={{ gridTemplateColumns: `56px repeat(${hours.length}, minmax(0, 1fr))` }}
      role="table"
      aria-label="Profit and loss by weekday and hour of entry"
    >
      {/* The corner, above the day labels. */}
      <div />
      {hours.map((h) => (
        <div className="heatmap-hour-label" key={h}>{hourLabel(h)}</div>
      ))}

      {dows.map((dow) => (
        <Row key={dow} dow={dow} hours={hours} cells={cells} maxAbs={maxAbs} money={money} />
      ))}
    </div>
  );
}

function Row(
  { dow, hours, cells, maxAbs, money }: {
    dow: number; hours: number[]; cells: HeatmapGrid['cells'];
    maxAbs: number; money: (n: number) => string;
  },
) {
  return (
    <>
      <div className="heatmap-day-label">{DOW_LABELS[dow]}</div>
      {hours.map((h) => {
        const c = cells.get(cellKey(dow, h));
        if (!c) {
          // ⚠ An empty cell is RENDERED, faintly, rather than omitted. The grid
          // has to keep its shape, and "never traded this window" is itself a
          // finding on a card about when you make money.
          return <div className="heatmap-cell empty" key={h} />;
        }
        const a = cellAlpha(c.pnl, maxAbs).toFixed(3);
        // ⚠ Literal rgb, not the tokens: the ramp needs a variable alpha and
        // `var(--green)` cannot carry one. These ARE the token values — `--green`
        // is #2e9dff — which is why the legend says blue.
        const bg = c.pnl > 0
          ? `rgba(46,157,255,${a})`
          : c.pnl < 0
            ? `rgba(246,104,94,${a})`
            : `rgba(160,160,160,${a})`;
        return (
          <div
            className="heatmap-cell"
            key={h}
            style={{ background: bg }}
            // ⚠ The tooltip carries the EXACT figure and the W/L split, because
            // the cell itself shows an abbreviated one. `-$5.5k` on screen and
            // `-$5,504.00 · 3W 8L` on hover is the whole point of the abbreviation
            // being safe.
            title={`${DOW_LABELS[dow]} ${hourLabel(h)} — ${c.count} trade${c.count === 1 ? '' : 's'} · `
              + `P&L ${money(c.pnl)} · ${c.wins}W ${c.losses}L`}
          >
            {cellLabel(c.pnl, money)}
          </div>
        );
      })}
    </>
  );
}
